'use client';

import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState([]);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
        const savedCart = localStorage.getItem('simple-shop-cart');
        if (savedCart) {
            try {
                setCartItems(JSON.parse(savedCart));
            } catch (e) {
                console.error('Failed to parse cart JSON');
            }
        }
    }, []);

    useEffect(() => {
        if (isMounted) {
            localStorage.setItem('simple-shop-cart', JSON.stringify(cartItems));
        }
    }, [cartItems, isMounted]);

    const addToCart = (product) => {
        setCartItems(prev => {
            const existing = prev.find(item => item.sku === product.sku);
            if (existing) {
                return prev.map(item =>
                    item.sku === product.sku
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (sku) => {
        setCartItems(prev => prev.filter(item => item.sku !== sku));
    };

    const updateQuantity = (sku, quantity) => {
        setCartItems(prev => prev.map(item =>
            item.sku === sku
                ? { ...item, quantity: Math.max(1, quantity) }
                : item
        ));
    };

    const clearCart = () => setCartItems([]);

    const cartTotal = cartItems.reduce((total, item) => total + (item.sell_price * item.quantity), 0);
    const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartTotal,
            cartCount,
            isMounted
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}
